import { validateCurrentSelectionsIncludeSourceSelections } from "./selectionValidation";

const NORMALIZERS = {
  assistiveDevice(value) {
    const normalizedValue = (value || "")
      .toString()
      .toLowerCase()
      .replace(/\(s\)/g, "")
      .replace(/[^a-z]/g, "");

    if (normalizedValue === "cane" || normalizedValue === "canes") {
      return "cane";
    }

    if (normalizedValue === "brace" || normalizedValue === "braces") {
      return "brace";
    }

    if (normalizedValue === "crutch" || normalizedValue === "crutches") {
      return "crutches";
    }

    if (normalizedValue === "walker") {
      return "walker";
    }

    return normalizedValue;
  },
};

function normalizeConfigList(value) {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

function getNormalizer(name) {
  return NORMALIZERS[name] || ((selection) => selection);
}

function questionMatchesRule(question, rule) {
  if (!question || !rule) return false;

  if (
    rule.sectionNumbers &&
    (!question.section || !rule.sectionNumbers.includes(question.section.number))
  ) {
    return false;
  }

  if (
    rule.sectionNumber &&
    (!question.section || question.section.number !== rule.sectionNumber)
  ) {
    return false;
  }

  if (rule.sectionTitle && (!question.section || question.section.title !== rule.sectionTitle)) {
    return false;
  }

  if (rule.question && question.question !== rule.question) {
    return false;
  }

  if (rule.type && question.type !== rule.type) {
    return false;
  }

  return true;
}

function isSelectedOption(option) {
  return !!(
    option &&
    option.main &&
    (option.answer === option.main ||
      option.subAnswer === option.main ||
      option.subAnswer3 === option.main)
  );
}

const OPTION_CHILD_KEYS = ["questions", "subItems", "subItems2", "subItems3", "options"];

function collectSelectedOptionLabelsFromNode(node, labels, optionKeys) {
  if (!node) return;

  if (!optionKeys && isSelectedOption(node)) {
    labels.push(node.main);
  }

  OPTION_CHILD_KEYS.forEach((childKey) => {
    if (Array.isArray(node[childKey])) {
      node[childKey].forEach((childNode) => {
        if (optionKeys && optionKeys.includes(childKey) && isSelectedOption(childNode)) {
          labels.push(childNode.main);
        }

        collectSelectedOptionLabelsFromNode(childNode, labels, optionKeys);
      });
    }
  });
}

function getSelectedOptionLabels(question, optionKeys) {
  const labels = [];
  collectSelectedOptionLabelsFromNode(question, labels, optionKeys);
  return labels;
}

function getSourceSelections(questions, config) {
  const rules = normalizeConfigList(config.sources);

  return rules.reduce((selections, rule) => {
    const matchingQuestions = (questions || []).filter((question) =>
      questionMatchesRule(question, rule)
    );

    return matchingQuestions.reduce(
      (nextSelections, question) =>
        nextSelections.concat(getSelectedOptionLabels(question, rule.optionKeys)),
      selections
    );
  }, []);
}

function getCurrentSelections(currentQuestion, requiredSelections, config) {
  if (
    config.current &&
    config.current.includeAllSourceSelectionsUnlessAnswer !== undefined
  ) {
    return currentQuestion.answer ===
      config.current.includeAllSourceSelectionsUnlessAnswer
      ? []
      : Array.from(requiredSelections);
  }

  return getSelectedOptionLabels(
    currentQuestion,
    config.current && config.current.optionKeys
  );
}

function formatLinkedSelectionValidationMessage({
  sourceSelections,
  missingSelections,
  config,
}) {
  if (config.message) {
    return config.message
      .replace("##sourceSelections##", sourceSelections.join(", "))
      .replace("##missingSelections##", missingSelections.join(", "));
  }

  const sourceLabel = config.sourceLabel || "the linked source questions";
  const targetLabel = config.targetLabel || "this question";

  return `Please select all options in ${targetLabel} that were selected in ${sourceLabel}. Selected in ${sourceLabel}: ${sourceSelections.join(
    ", "
  )}. Missing from ${targetLabel}: ${missingSelections.join(", ")}.`;
}

export function validateLinkedSelectionDependency(questions, currentQuestion) {
  const config =
    currentQuestion && currentQuestion.linkedSelectionValidation;

  if (!config) {
    return null;
  }

  const sourceSelections = getSourceSelections(questions, config);
  const currentSelections = getCurrentSelections(
    currentQuestion,
    sourceSelections,
    config
  );

  return validateCurrentSelectionsIncludeSourceSelections({
    questions,
    currentQuestion,
    isSourceQuestion: () => false,
    isCurrentQuestion: () => true,
    getSourceSelections: () => [],
    getCurrentSelections: () => [],
    normalizeSelection: getNormalizer(config.normalizer),
    message: ({ sourceSelections, missingSelections }) =>
      formatLinkedSelectionValidationMessage({
        sourceSelections,
        missingSelections,
        config,
      }),
    sourceSelections,
    currentSelections,
  });
}
